import { zodResolver } from '@hookform/resolvers/zod'
import TranslateIcon from '@mui/icons-material/Translate'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid2,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import { useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form'
import { FormContainer, TextFieldElement } from 'react-hook-form-mui'
import { DatePickerElement } from 'react-hook-form-mui/date-pickers'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'

import MainLayout from '~/components/layouts/MainLayout'
import { hasGraphqlOperationError } from '~/features/shared/utils/graphql-response-utils'
import { queries } from '~/queries'
import {
  createAnnouncement,
  getTranslation,
  updateAnnouncement,
} from '~/services/announcements-services'
import { getDateMaskFormat } from '~/utils/format-date'
import { type PlatformLanguage } from '~/utils/types/i18n'
import { excludeFalsyWithMessage } from '~/utils/zod'

interface SupportedLanguage {
  code: string
  name: string
}

const MAIN_LANGUAGE = 'es'
const MAX_TEXT_LENGTH = 350

export default function AnnouncementForm() {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { id } = useParams()

  const [loadingTranslations, setLoadingTranslations] = useState(false)

  const isEditing = Boolean(id)

  const schema = z.object({
    date: z
      .custom<moment.Moment>(value => moment.isMoment(value))
      .nullable()
      .transform(excludeFalsyWithMessage(t('validation.validDate'))),
    annoucements: z.array(
      z.object({
        title: z
          .string()
          .trim()
          .min(1, t('validation.fieldIsRequired'))
          .max(MAX_TEXT_LENGTH, t('validation.maxLength', { max: MAX_TEXT_LENGTH })),
        content: z
          .string()
          .trim()
          .min(1, t('validation.fieldIsRequired'))
          .max(MAX_TEXT_LENGTH, t('validation.maxLength', { max: MAX_TEXT_LENGTH })),
        langCode: z.string(),
        langName: z.string(),
      }),
    ),
  })

  type AnnouncementFormSchema = z.input<typeof schema>
  type AnnouncementFormOutput = z.infer<typeof schema>
  type Announcement = AnnouncementFormSchema['annoucements'][number]
  type AnnouncementField = Announcement & {
    id: string
  }

  const announcementsForm = useForm<AnnouncementFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: { date: null, annoucements: [] },
  })

  const listOfAnnouncements = useQuery({
    ...queries.announcements.getAnnouncement(Number(id)),
    enabled: Boolean(id),
  })

  const arrayFields = useFieldArray({
    control: announcementsForm.control,
    name: 'annoucements',
  })

  const supportedTranslationLanguages = useQuery({
    ...queries.announcements.listSupportedTranslationLanguages(),
    select: data => {
      const languages = data.data.data.listSupportedTranslationLanguages.map(
        lang => ({
          code: lang.code,
          name: lang.description,
        }),
      )
      // Sort languages to put the main language (Spanish) first
      return languages.sort((a, b) => {
        if (a.code === MAIN_LANGUAGE) return -1
        if (b.code === MAIN_LANGUAGE) return 1
        return 0
      })
    },
  })
  const languages = supportedTranslationLanguages.isSuccess
    ? supportedTranslationLanguages.data
    : []

  const createAnnouncementMutation = useMutation({
    mutationFn: createAnnouncement,

    onSuccess: response => {
      if (hasGraphqlOperationError(response.data)) {
        enqueueSnackbar(t('views.announcement.error'), {
          variant: 'error',
        })
        return
      }
      enqueueSnackbar(t('views.announcement.saved'), {
        variant: 'success',
      })
      navigate('/announcements')
    },
    onError: () => {
      enqueueSnackbar(t('views.announcement.error'), {
        variant: 'error',
      })
    },
  })

  const updateAnnouncementMutation = useMutation({
    mutationFn: updateAnnouncement,

    onSuccess: response => {
      if (hasGraphqlOperationError(response.data)) {
        enqueueSnackbar(t('views.announcement.error'), {
          variant: 'error',
        })
        return
      }
      enqueueSnackbar(t('views.announcement.saved'), {
        variant: 'success',
      })
      navigate('/announcements')
    },
    onError: () => {
      enqueueSnackbar(t('views.announcement.error'), {
        variant: 'error',
      })
    },
  })
  const isSaving =
    createAnnouncementMutation.isLoading || updateAnnouncementMutation.isLoading

  async function onSubmit(data: AnnouncementFormOutput) {
    const announcementDate = data.date.toISOString().split('T').at(0)
    if (!announcementDate) return

    if (!supportedTranslationLanguages.isSuccess) return

    if (isEditing && !listOfAnnouncements.isSuccess) return

    const announcementModels = supportedTranslationLanguages.data.map(
      (lang, idx) => {
        const existing =
          isEditing && listOfAnnouncements.isSuccess
            ? listOfAnnouncements.data.data.data.getAnnouncement.find(
                a => a.lang === lang.code,
              )
            : null

        const formAnnouncement = data.annoucements[idx]

        return {
          id: existing?.id,
          title: formAnnouncement?.title ?? '',
          content: formAnnouncement?.content ?? '',
          announcementDate,
          lang: lang.code as PlatformLanguage,
        }
      },
    )

    if (isEditing) {
      updateAnnouncementMutation.mutate(announcementModels)
    } else {
      createAnnouncementMutation.mutate(announcementModels)
    }
  }

  useEffect(() => {
    if (!supportedTranslationLanguages.isSuccess) return

    const isEditMode = isEditing && listOfAnnouncements.isSuccess
    const announcementsList = isEditMode
      ? listOfAnnouncements.data.data.data.getAnnouncement
      : []

    const getAnnouncementByLang = (langCode: string) =>
      announcementsList.find(a => a.lang === langCode)

    const announcementsData: Announcement[] =
      supportedTranslationLanguages.data.map(lang => {
        const current = getAnnouncementByLang(lang.code)

        return {
          langCode: lang.code,
          langName: lang.name,
          title: current?.title ?? '',
          content: current?.content ?? '',
        }
      })

    const firstAnnouncement = announcementsList.at(0)
    const dateValue =
      isEditMode && firstAnnouncement
        ? moment(firstAnnouncement.announcementDate)
        : null

    announcementsForm.reset({
      date: dateValue,
      annoucements: announcementsData,
    })
  }, [
    isEditing,
    announcementsForm,
    supportedTranslationLanguages.data,
    supportedTranslationLanguages.isSuccess,
    listOfAnnouncements.isSuccess,
    listOfAnnouncements.data,
  ])

  async function generateTranslations(
    formAnnouncements: Announcement[],
    languages: SupportedLanguage[],
  ) {
    const base = formAnnouncements.find(a => a.langCode === MAIN_LANGUAGE)
    if (!base) throw new Error('Spanish text not available')

    const updates: Announcement[] = structuredClone(formAnnouncements)
    const languagesWithoutMainLanguage = languages.filter(
      lang => lang.code !== MAIN_LANGUAGE,
    )

    await Promise.all(
      languagesWithoutMainLanguage.map(async lang => {
        const [titleRes, contentRes] = await Promise.all([
          getTranslation(base.title, lang.code, MAIN_LANGUAGE),
          getTranslation(base.content, lang.code, MAIN_LANGUAGE),
        ])
        const title = titleRes.data.data.getTranslation
        const content = contentRes.data.data.getTranslation
        const idx = updates.findIndex(a => a.langCode === lang.code)
        if (idx !== -1) {
          updates[idx].title = title
          updates[idx].content = content
        }
      }),
    )
    return updates
  }

  const translationMutation = useMutation({
    mutationFn: async () => {
      const formAnnouncements = announcementsForm.getValues('annoucements')
      return await generateTranslations(formAnnouncements, languages)
    },
    onSuccess: updates => {
      announcementsForm.setValue('annoucements', updates)
    },
    onError: () => {
      enqueueSnackbar(t('views.announcement.translationError'), {
        variant: 'error',
      })
    },
    onSettled: () => {
      setLoadingTranslations(false)
    },
  })

  return (
    <MainLayout>
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Typography variant="h4" fontWeight="bold" mb={2}>
          {id ? t('views.announcement.edit') : t('views.announcement.create')}
        </Typography>
        <FormContainer formContext={announcementsForm}>
          <DatePickerElement
            label={t('views.announcement.date')}
            control={announcementsForm.control}
            name="date"
            closeOnSelect
            format={getDateMaskFormat()}
            slotProps={{
              actionBar: { actions: ['clear', 'cancel', 'accept', 'today'] },
            }}
          />

          <Stack direction="row" spacing={2} mb={2} sx={{ mt: 2 }}>
            <LoadingButton
              variant="outlined"
              loading={loadingTranslations}
              startIcon={<TranslateIcon />}
              onClick={() => {
                setLoadingTranslations(true)
                translationMutation.mutate()
              }}
            >
              {t('views.announcement.generateTranslations')}
            </LoadingButton>
          </Stack>
          <Grid2 display="flex" flexDirection="row" container spacing={2}>
            {supportedTranslationLanguages.isFetching
              ? Array.from({ length: 9 }).map((_, idx) => (
                  <Grid2 gap={2} size={{ xs: 12, md: 4 }} key={idx}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          mb={2}
                        >
                          <Skeleton
                            variant="rectangular"
                            width={60}
                            height={32}
                          />
                          <Skeleton variant="text" width={100} />
                        </Stack>
                        <Stack direction="column" spacing={1} gap={2}>
                          <Skeleton variant="text" width="100%" height={40} />
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={80}
                          />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid2>
                ))
              : arrayFields.fields.map((field: AnnouncementField, index) => (
                  <LanguageCard {...field} key={field.id} index={index} />
                ))}
          </Grid2>

          <Stack direction="row" spacing={2} mt={4}>
            <Button
              variant="outlined"
              onClick={() => {
                navigate('/announcements')
              }}
              disabled={isSaving}
            >
              {t('general.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              onClick={announcementsForm.handleSubmit(onSubmit)}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={20} /> : t('general.save')}
            </Button>
          </Stack>
        </FormContainer>
      </Container>
    </MainLayout>
  )
}

interface LanguageCardProps {
  index: number
  langName: string
  langCode: string
}

function LanguageCard({ langName, langCode, index }: LanguageCardProps) {
  const { t } = useTranslation()
  const { control } = useFormContext()

  const content: string =
    useWatch({ control, name: `annoucements.${index}.content` }) ?? ''

  return (
    <Grid2 gap={2} size={{ xs: 12, md: 4 }}>
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Chip label={langCode.toUpperCase()} color="primary" />
            <Typography variant="subtitle2" color="text.secondary">
              {langName}
            </Typography>
          </Stack>
          <Stack direction="column" spacing={1} gap={2}>
            <TextFieldElement
              name={`annoucements.${index}.title`}
              label={t('views.announcement.title')}
              variant="outlined"
              required
              fullWidth
              inputProps={{ maxLength: MAX_TEXT_LENGTH }}
            />
            <TextFieldElement
              name={`annoucements.${index}.content`}
              label={t('views.announcement.content')}
              variant="outlined"
              required
              fullWidth
              multiline
              rows={3}
              inputProps={{ maxLength: MAX_TEXT_LENGTH }}
              helperText={`${content.length}/${MAX_TEXT_LENGTH}`}
            />
          </Stack>
        </CardContent>
      </Card>
    </Grid2>
  )
}
